<template>
    <div v-if="carbonData" class="panel-wrapper">
        <div class="content-row shadow-box">
            <!-- 상단 row -->
            <div class="col-3 fixHeight">
                <template v-if="hasGradeData">
                    <energy-efficiency-chart ref="evergyEffChart" :data="carbonData" :timeLabel="timeLabel" />
                </template>
                <div v-else class="empty-placeholder">
                    <common-title-panel
                        :title="'건축물에너지효율등급'"
                        :subTitle="`단위면적당 1차에너지 소요량 (${carbonData.values.grade.unitInfo?.unit || '-'} · ${timeLabel})`"
                    ></common-title-panel>
                    <div class="placeholder-content">
                        <span class="placeholder-icon">
                            <i class="fas fa-exclamation-circle"></i>
                        </span>
                        <span class="placeholder-text">표시할 데이터가 없습니다.</span>
                    </div>
                </div>
            </div>
            <div class="col-3" style="height: 100%; min-height: 0">
                <ZebGradeChart ref="ZebGradeChartRef" :data="carbonData" />
            </div>
            <div class="col-3 fixHeight">
                <template v-if="hasGseedData">
                    <GseedGradeChart ref="gseedGradeChartRef" :viewModel="gseedViewModel" />
                </template>
                <div v-else class="empty-placeholder">
                    <common-title-panel
                        :title="'녹색건축인증 (G-SEED)'"
                        :subTitle="'7개 항목 인증 점수'"
                    ></common-title-panel>
                    <div class="placeholder-content">
                        <span class="placeholder-icon">
                            <i class="fas fa-leaf"></i>
                        </span>
                        <span class="placeholder-text">G-SEED 데이터가 없습니다.</span>
                    </div>
                </div>
                <!-- 레거시 CO2: gseedMock.meta.showLegacyCo2BySource=true -->
                <div v-if="showLegacyCo2BySource" v-show="false" class="stack-table-container">
                    <template v-if="hasCo2EmissionData">
                        <StackChart
                            ref="CO2UsageChartRef"
                            :title="'단위면적당 에너지원별 CO2 배출량'"
                            :subTitle="`(${carbonData.values.co2Emission.unitInfo.unit} · ${timeLabel} )`"
                            :chartHeight="stackChartHeight"
                            :data="carbonData.values.co2Emission.chartData"
                            :color="analyzeConfig.colorSet"
                            :xAxisLabel="xAxisLabel"
                        />
                        <div style="margin-top: 10px">
                            <CompTable :tableData="carbonData.values.co2Emission.tableData" :timeDsvn="timeDsvn" />
                        </div>
                    </template>
                    <div v-else class="empty-placeholder">
                        <common-title-panel
                            :title="'단위면적당 에너지원별 CO2 배출량'"
                            :subTitle="`(${carbonData.values.co2Emission.unitInfo.unit} · ${timeLabel} )`"
                        ></common-title-panel>
                        <div class="placeholder-content">
                            <span class="placeholder-icon">
                                <i class="fas fa-exclamation-circle"></i>
                            </span>
                            <span class="placeholder-text">표시할 데이터가 없습니다.</span>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-3 fixHeight">
                <template v-if="hasGhgEmissionData">
                    <div class="emissionCap">
                        <BuildingEmissionCap
                            ref="ze"
                            :data="carbonData"
                            :chartHeight="stackChartHeight"
                            :timeLabel="timeLabel"
                        />
                        <div style="margin-top: 10px">
                            <GreenhouseGasTable :data="carbonData" />
                        </div>
                    </div>
                </template>
                <div v-else class="empty-placeholder">
                    <common-title-panel
                        :title="'건물온실가스총량제'"
                        :subTitle="`단위면적당 탄소배출량 (${carbonData.values.ghgEmission?.unitInfo?.unit || '-'} · ${timeLabel})`"
                    ></common-title-panel>
                    <div class="placeholder-content">
                        <span class="placeholder-icon">
                            <i class="fas fa-exclamation-circle"></i>
                        </span>
                        <span class="placeholder-text">표시할 데이터가 없습니다.</span>
                    </div>
                </div>
            </div>
        </div>
        <div class="content-row shadow-box">
            <!-- 하단 row -->
            <div class="col-3">
                <div class="stack-table-container">
                    <template v-if="hasUsageData">
                        <StackChart
                            ref="energyUsageChart"
                            :title="'단위면적당 용도별 1차 에너지 소비량'"
                            :subTitle="`(${carbonData.values.usage.unitInfo.unit} · ${timeLabel})`"
                            :chartHeight="stackChartHeight"
                            :data="carbonData.values.usage.chartData"
                            :color="analyzeConfig.colorSet"
                            :xAxisLabel="xAxisLabel"
                        />
                        <div style="margin-top: 10px">
                            <CompTable :tableData="carbonData.values.usage.tableData" :timeDsvn="timeDsvn" />
                        </div>
                    </template>
                    <div v-else class="empty-placeholder">
                        <common-title-panel
                            :title="'단위면적당 용도별 1차 에너지 소비량'"
                            :subTitle="`(${carbonData.values.usage.unitInfo.unit} · ${timeLabel})`"
                        ></common-title-panel>
                        <div class="placeholder-content">
                            <span class="placeholder-icon">
                                <i class="fas fa-exclamation-circle"></i>
                            </span>
                            <span class="placeholder-text">표시할 데이터가 없습니다.</span>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-3">
                <div class="stack-table-container">
                    <template v-if="hasRenewableData">
                        <StackChart
                            ref="selfRateChartRef"
                            :title="'신재생 에너지원별 자립률 구성'"
                            :subTitle="`(% · ${timeLabel})`"
                            :chartHeight="stackChartHeight"
                            :data="carbonData.values.renewable.chartData"
                            :color="analyzeConfig.colorSet"
                            :xAxisLabel="xAxisLabel"
                        />
                        <div style="margin-top: 10px">
                            <CompTable :tableData="carbonData.values.renewable.tableData" :timeDsvn="timeDsvn" />
                        </div>
                    </template>
                    <div v-else class="empty-placeholder">
                        <common-title-panel
                            :title="'신재생 에너지원별 자립률 구성'"
                            :subTitle="`(% · ${timeLabel})`"
                        ></common-title-panel>
                        <div class="placeholder-content">
                            <span class="placeholder-icon">
                                <i class="fas fa-exclamation-circle"></i>
                            </span>
                            <span class="placeholder-text">표시할 데이터가 없습니다.</span>
                        </div>
                    </div>
                </div>
            </div>

            <div class="col-6">
                <template v-if="timeDsvn === 'MONTH'">
                    <template v-if="hasCo2TrendData">
                        <TrendChart
                            ref="CO2TrendChartRef"
                            :title="'에너지원별 CO2 배출량 추이'"
                            :subTitle="`(${carbonData.values.co2Trend.unit} vs ℃)`"
                            :chartHeight="25"
                            :data="carbonData.values.co2Trend.chartData"
                            :color="analyzeConfig.colorSet"
                            :xAxisLabel="carbonData.values.co2Trend.regDt"
                            :showLabel="false"
                            explanation="배출량"
                        />
                    </template>
                    <div v-else class="empty-placeholder">
                        <common-title-panel
                            :title="'에너지원별 CO2 배출량 추이'"
                            :subTitle="`(${carbonData.values.co2Trend?.unit || '-'} vs ℃)`"
                        ></common-title-panel>
                        <div class="placeholder-content">
                            <span class="placeholder-icon">
                                <i class="fas fa-exclamation-circle"></i>
                            </span>
                            <span class="placeholder-text">표시할 데이터가 없습니다.</span>
                        </div>
                    </div>
                </template>
                <template v-else-if="timeDsvn === 'YEAR'">
                    <template v-if="hasEnergyGradeTrendData">
                        <energy-grade-chart-panel
                            ref="energyGradeRef"
                            :title="'건축물 에너지 등급 추이'"
                            chartHeight="85%"
                            :color="analyzeConfig.colorSet"
                            :data="carbonData.values.energyGradeTrend"
                            :certifiedZEBGrade="carbonData.values.zebGrade.certifiedZEBGrade"
                            :showLabel="false"
                            :style="{ height: '100%' }"
                        />
                    </template>
                    <div v-else class="empty-placeholder">
                        <common-title-panel :title="'건축물 에너지 등급 추이'" :subTitle="''"></common-title-panel>
                        <div class="placeholder-content">
                            <span class="placeholder-icon">
                                <i class="fas fa-exclamation-circle"></i>
                            </span>
                            <span class="placeholder-text">표시할 데이터가 없습니다.</span>
                        </div>
                    </div>
                </template>
            </div>
        </div>
    </div>
</template>

<script>
import StackChart from "../charts/StackChart";
import TrendChart from "../charts/TrendChart";
import CompTable from "../tables/CompTable";
import EnergyEfficiencyChart from "../charts/EnergyEfficiencyChart.vue";
import BuildingEmissionCap from "../charts/BuildingEmissionCapChart.vue";
import GreenhouseGasTable from "../tables/GreenhouseGasTable.vue";
import EnergyGradeChartPanel from "../charts/EnergyGradeChartPanel.vue";

import ZebGradeChart from "../charts/ZebGradeChart";
import GseedGradeChart from "../charts/GseedGradeChart.vue";
import { getShowLegacyCo2BySource } from "../data/gseedData";
import analyzeConfig from "../analyzeConfig.json";
import { isEmptyChartAndTableData } from "@/utils/dataValidation";
import CommonTitlePanel from "./CommonTitlePanel.vue";

export default {
    props: {
        carbonData: {
            type: Object,
            default: () => {},
        },
    },
    components: {
        StackChart,
        TrendChart,
        CompTable,
        EnergyEfficiencyChart,
        BuildingEmissionCap,
        ZebGradeChart,
        GseedGradeChart,
        GreenhouseGasTable,
        EnergyGradeChartPanel,
        CommonTitlePanel,
    },
    data() {
        return {
            analyzeConfig: null,
            xAxisLabelMonth: ["전월", "당월", "전년동월"],
            stackChartHeight: 15,
            chartRefs: [], // 차트 refs 추적
            showLegacyCo2BySource: getShowLegacyCo2BySource(),
        };
    },
    computed: {
        gseedViewModel() {
            return this.carbonData?.values?.gseed ?? null;
        },
        hasGseedData() {
            return Boolean(this.gseedViewModel?.hasData);
        },
        timeDsvn() {
            return this.carbonData?.headers[0]?.timeDsvn;
        },
        timeLabel() {
            return this.carbonData?.headers[0]?.timeDsvn === "MONTH" ? "월" : "년";
        },
        xAxisLabel() {
            const label = this.timeDsvn == "YEAR" ? ["작년", "올해", "재작년"] : ["전월", "당월", "전년동월"];
            return label ?? [];
        },
        hasUsageData() {
            if (!this.carbonData?.values?.usage) return false;
            return !isEmptyChartAndTableData(
                {
                    chartData: this.carbonData.values.usage.chartData,
                    tableData: this.carbonData.values.usage.tableData,
                },
                this.timeDsvn,
            );
        },
        hasRenewableData() {
            if (!this.carbonData?.values?.renewable) return false;
            return !isEmptyChartAndTableData(
                {
                    chartData: this.carbonData.values.renewable.chartData,
                    tableData: this.carbonData.values.renewable.tableData,
                },
                this.timeDsvn,
            );
        },
        hasCo2EmissionData() {
            if (!this.carbonData?.values?.co2Emission) return false;
            return !isEmptyChartAndTableData(
                {
                    chartData: this.carbonData.values.co2Emission.chartData,
                    tableData: this.carbonData.values.co2Emission.tableData,
                },
                this.timeDsvn,
            );
        },
        hasGradeData() {
            if (!this.carbonData?.values?.grade) return false;
            const grade = this.carbonData.values.grade;
            // measuredGrade, certifiedGrade, totalOperatingGrade가 모두 null이거나 undefined인지 확인
            return grade.measuredGrade != null || grade.certifiedGrade != null || grade.totalOperatingGrade != null;
        },
        hasGhgEmissionData() {
            if (!this.carbonData?.values?.ghgEmission) return false;
            return this.carbonData.values.ghgEmission.buildingGHG != null;
        },
        hasCo2TrendData() {
            if (!this.carbonData?.values?.co2Trend?.chartData) return false;
            const chartData = this.carbonData.values.co2Trend.chartData;
            // 모든 키들(전기, 가스, total, outerTemp 등)의 값이 모두 0이거나 null인지 확인
            const keys = Object.keys(chartData);
            if (keys.length === 0) return false;
            return keys.some((key) => {
                const arr = chartData[key];
                if (!arr || !Array.isArray(arr)) return false;
                return arr.some((item) => item != null && item !== 0);
            });
        },
        hasEnergyGradeTrendData() {
            if (!this.carbonData?.values?.energyGradeTrend?.zeb?.chartData) return false;
            const zebChartData = this.carbonData.values.energyGradeTrend.zeb.chartData;
            // selfRate나 zebGrade 중 하나라도 null이 아닌 값이 있는지 확인
            const selfRate = zebChartData.selfRate;
            const zebGrade = zebChartData.zebGrade;
            if (selfRate && Array.isArray(selfRate)) {
                if (selfRate.some((item) => item != null)) return true;
            }
            if (zebGrade && Array.isArray(zebGrade)) {
                if (zebGrade.some((item) => item != null)) return true;
            }
            return false;
        },
    },
    watch: {},
    mounted() {},
    destroyed() {
        // console.log(`[DESTROYED] CarbonAnalysisPanel`);
    },

    created() {
        this.analyzeConfig = analyzeConfig;
    },
    methods: {
        /* 차트 refs 수집 */
        collectChartRefs() {
            this.chartRefs = [
                "evergyEffChart",
                "energyUsageChart",
                "selfRateChartRef",
                "ze",
                "CO2UsageChartRef",
                "gseedGradeChartRef",
                "CO2TrendChartRef",
                "ZebGradeChartRef",
                "energyGradeRef",
            ];
        },

        /* 모든 차트 정리 */
        cleanupAllCharts() {
            this.chartRefs.forEach((refName) => {
                const chartRef = this.$refs[refName];
                if (chartRef && typeof chartRef.cleanupChart === "function") {
                    chartRef.cleanupChart();
                }
            });
        },

        resizeChart() {
            if (this.$refs.evergyEffChart && this.hasGradeData) this.$refs.evergyEffChart.resizeChart();
            if (this.$refs.energyUsageChart) this.$refs.energyUsageChart.resizeChart();
            if (this.$refs.selfRateChartRef) this.$refs.selfRateChartRef.resizeChart();
            if (this.$refs.ze && this.hasGhgEmissionData) this.$refs.ze.resizeChart();
            if (this.$refs.CO2UsageChartRef) this.$refs.CO2UsageChartRef.resizeChart();
            if (this.$refs.gseedGradeChartRef && this.hasGseedData) this.$refs.gseedGradeChartRef.resizeChart();
            if (this.$refs.CO2TrendChartRef && this.hasCo2TrendData) this.$refs.CO2TrendChartRef.resizeChart();
            if (this.$refs.energyGradeRef && this.hasEnergyGradeTrendData) this.$refs.energyGradeRef.resizeChart();
            if (this.$refs.ZebGradeChartRef) this.$refs.ZebGradeChartRef.resizeChart();
        },
    },
};
</script>

<style scoped>
.panel-wrapper {
    width: 100%;
    height: 100%;
    padding: 10px;
    display: flex;
    flex-direction: column;
    gap: 10px; /* row 간격 */
}

.content-row {
    display: flex;
    width: 100%;
    height: calc(50% - 5px); /* 두 영역 사이 gap 을 감안한 정확한 50% 분할 */
    padding: 10px;
    box-sizing: border-box;
    background-color: white;
    border-radius: 10px;
}

.shadow-box {
    box-shadow: 0 0 2px 2px rgba(0, 0, 0, 0.15);
}
.stack-table-container {
    width: 100%;
    height: 100%;
}

.fixHeight {
    height: 100%;
    max-height: 100%;
    overflow: hidden;
}
.emissionCap {
    width: 100%;
    height: 100%;
}

.empty-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    gap: 0;
    color: #999;
}

.placeholder-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    width: 100%;
}

.placeholder-icon {
    font-size: 2rem;
    color: #ccc;
}

.placeholder-text {
    font-size: 0.9rem;
    color: #666;
}
</style>
